import React from 'react';
import { WifiOff, Wifi, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';
import { useOfflineFormsStore } from '@/stores/offlineFormsStore';

export function OfflineStatusBanner() {
  const { 
    isOnline, 
    isOffline, 
    quality, 
    networkType, 
    offlineDuration,
    shouldUseOfflineMode 
  } = useOfflineStatus();
  
  const { pendingSubmissions } = useOfflineFormsStore();
  const pendingCount = pendingSubmissions.filter(s => s.status === 'pending').length;

  // Don't show banner if everything is normal
  if (isOnline && quality === 'good' && pendingCount === 0) {
    return null;
  }

  const formatOfflineDuration = (duration: number) => {
    if (duration < 60000) return 'Just now';
    const minutes = Math.floor(duration / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Offline mode
  if (isOffline) {
    return (
      <div className="bg-orange-50 border-b border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <WifiOff className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm font-medium text-orange-800">
                  You're offline
                </p>
                <p className="text-xs text-orange-600">
                  {offlineDuration > 0 && `Offline for ${formatOfflineDuration(offlineDuration)}`}
                  {pendingCount > 0 && ` • ${pendingCount} forms waiting to sync`}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-orange-600 font-medium">
                Forms can still be submitted
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Poor connection
  if (quality === 'poor') {
    return (
      <div className="bg-yellow-50 border-b border-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                Slow connection detected
                {networkType && ` (${networkType})`}
              </p>
            </div>
            <span className="text-xs text-yellow-600">
              Using offline mode
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Pending submissions (online but has queue)
  if (pendingCount > 0) {
    return (
      <div className="bg-blue-50 border-b border-blue-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-blue-600 animate-spin" />
              <p className="text-sm text-blue-800">
                Syncing {pendingCount} form submission{pendingCount !== 1 ? 's' : ''}
              </p>
            </div>
            <Wifi className="h-4 w-4 text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default OfflineStatusBanner;